import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BarChart3, BriefcaseBusiness, Coins, FileText, Heart, LockKeyhole, Paperclip, Pencil, Receipt, Search, TrendingUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/site/AppHeader";
import { getPartnerDashboard, listMyJobs } from "@/lib/partner.functions";
import { getMyPartnerEntitlements } from "@/lib/memberships.functions";
import { bookingStatusLabel, gbp, ukDate } from "@/lib/format";
import { StatusPill } from "@/components/StatusPill";
import {
  addBusinessCustomerNote,
  createPartnerExpense,
  createPartnerInvoice,
  deletePartnerExpense,
  getPartnerExpenseReceiptUrl,
  getPartnerFinance,
  listBusinessCustomers,
  scheduleBusinessCustomerFollowup,
  sendPartnerInvoice,
  setBusinessCustomerFollowupStatus,
  setPartnerInvoiceStatus,
  updatePartnerExpense,
} from "@/lib/partner-business.functions";
import { removeUploadedFile, uploadBusinessReceipt } from "@/lib/uploads";

export const Route = createFileRoute("/_authenticated/partner/business/$module")({
  validateSearch: (search: Record<string, unknown>) => ({
    bookingId: typeof search["bookingId"] === "string" ? search["bookingId"] : undefined,
    create: search["create"] === "invoice" ? "invoice" as const : undefined,
    customerName: typeof search["customerName"] === "string" ? search["customerName"].slice(0, 160) : undefined,
  }),
  head: ({ params }) => ({ meta: [{ title: titleFor(params.module) + " — Shields Partner" }, { name: "robots", content: "noindex" }] }),
  component: PartnerWorkspaceModule,
});

const MODULES = {
  customers: { title: "Customers", kicker: "CUSTOMERS", description: "Customers, conversations and job history.", entitlement: "crm", icon: Users },
  quotes: { title: "Quotes", kicker: "QUOTES", description: "Create and manage job quotes.", entitlement: "quotes", icon: FileText },
  invoices: { title: "Invoices", kicker: "INVOICES", description: "Create and manage invoices.", entitlement: "invoices", icon: Receipt },
  expenses: { title: "Expenses", kicker: "EXPENSES", description: "Keep your business costs organised.", entitlement: "expenses", icon: Coins },
  profitability: { title: "Job profit", kicker: "JOB PROFIT", description: "Revenue and recorded costs by job.", entitlement: "job_costing", icon: TrendingUp },
  analytics: { title: "Reports", kicker: "REPORTS", description: "A simple view of your Shields work.", entitlement: "reports", icon: BarChart3 },
} as const;
type ModuleKey = keyof typeof MODULES;

function titleFor(value: string) { return value in MODULES ? MODULES[value as ModuleKey].title : "Business workspace"; }

function PartnerWorkspaceModule() {
  const { module } = Route.useParams();
  const search = Route.useSearch();
  const config = MODULES[module as ModuleKey];
  const dashboard = useQuery({ queryKey: ["partner-dashboard"], queryFn: () => getPartnerDashboard() });
  const jobs = useQuery({ queryKey: ["partner-jobs"], queryFn: () => listMyJobs() });
  const entitlements = useQuery({ queryKey: ["partner-entitlements"], queryFn: () => getMyPartnerEntitlements() });

  if (!config) return <NotFound />;
  const Icon = config.icon;
  const entitled = Boolean(entitlements.data?.keys?.includes(config.entitlement));
  const rows = jobs.data ?? [];
  const completed = rows.filter((job: any) => ["completed","closed"].includes(job.status));
  const active = rows.filter((job: any) => !["completed","closed","cancelled","declined","expired"].includes(job.status));

  return <div className="min-h-screen bg-background pb-24">
    <AppHeader workspace="partner" />
    <main className="business-workspace">
      <Link to="/partner/business" className="business-back"><ArrowLeft /> Business tools</Link>
      <header className="business-workspace-hero">
        <div><p className="technical-label">{config.kicker}</p><h1>{config.title}</h1><p>{config.description}</p></div>
        <span><Icon aria-hidden /></span>
      </header>

      {!entitlements.data ? <p className="business-empty">Checking workspace access…</p> : !entitled ? <section className="business-locked">
        <LockKeyhole /><div><p className="technical-label">MEMBERSHIP</p><h2>{config.title}</h2><p>Your current membership does not include this tool.</p><Link to="/plans">View Partner memberships <ArrowRight /></Link></div>
      </section> : <>
        <section className="business-metric-grid">
          <Metric label="ACTIVE JOBS" value={String(active.length)} />
          <Metric label="COMPLETED" value={String(completed.length)} />
          <Metric label="REVENUE RECORDED" value={dashboard.data ? gbp(dashboard.data.earnedPence) : "—"} />
          <Metric label="LIVE OPPORTUNITIES" value={dashboard.data ? String(dashboard.data.activeOffers) : "—"} />
        </section>
        <WorkspaceBody module={module as ModuleKey} rows={rows as any[]} initialBookingId={search.bookingId} initialCustomerName={search.customerName} createInvoice={search.create === "invoice"} />
      </>}
    </main>
  </div>;
}

function WorkspaceBody({ module, rows, initialBookingId, initialCustomerName, createInvoice }: { module: ModuleKey; rows: any[]; initialBookingId?: string | undefined; initialCustomerName?: string | undefined; createInvoice?: boolean }) {
  if (module === "customers") return <CustomersPanel />;
  if (module === "invoices") return <InvoicesPanel initialBookingId={initialBookingId} initialCustomerName={initialCustomerName} autoOpen={Boolean(createInvoice)} />;
  if (module === "expenses") return <ExpensesPanel />;
  if (module === "profitability") return <CostingPanel />;
  if (module === "analytics") {
    const completed = rows.filter((row) => ["completed","closed"].includes(row.status));
    const terminal = new Set(["completed","closed","cancelled","declined","expired"]);
    const active = rows.filter((row) => !terminal.has(row.status));
    const revenue = completed.reduce((sum, row) => sum + Number(row.agreed_price_pence ?? 0), 0);
    return <section className="business-data-panel">
      <header><div><p className="technical-label">LIVE OPERATIONS</p><h2>Work performance</h2></div></header>
      <div className="business-finance-grid"><Metric label="COMPLETED REVENUE" value={gbp(revenue)} /><Metric label="JOBS ASSIGNED" value={String(rows.length)} /><Metric label="COMPLETED JOBS" value={String(completed.length)} /><Metric label="ACTIVE JOBS" value={String(active.length)} /></div>
      <p className="business-footnote">These figures use genuine Shields jobs only. No estimated revenue, fake chart data or invented performance is added.</p>
    </section>;
  }


  return <section className="business-data-panel">
    <header><div><h2>{MODULES[module].title}</h2></div><span>{rows.length}</span></header>
    <div className="business-record-list">
      {rows.slice(0, 12).map((row) => <Link key={row.id} to="/partner/jobs/$bookingId" params={{ bookingId: row.id }} className="business-live-row">
        <span className="business-record-icon"><BriefcaseBusiness /></span>
        <span><strong>{(row.services as { name?: string } | null)?.name ?? "Shields job"}</strong><small>{row.reference} · {row.postcode}{row.preferred_date ? " · " + ukDate(row.preferred_date) : ""}</small></span>
        <span className="business-row-value">{row.agreed_price_pence ? gbp(row.agreed_price_pence) : "Price pending"}</span>
        <StatusPill status={row.status} label={bookingStatusLabel(row.status)} />
        <ArrowRight />
      </Link>)}
      {!rows.length && <div className="business-empty"><strong>No records yet</strong><span>Real Shields jobs will populate this workspace automatically.</span></div>}
    </div>
  </section>;
}

function CustomersPanel() {
  const queryClient = useQueryClient();
  const customersQuery = useQuery({
    queryKey: ["business-customers"],
    queryFn: () => listBusinessCustomers(),
  });
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [followup, setFollowup] = useState({ title: "", dueAt: "", purpose: "service", channel: "manual", note: "" });
  const [busy, setBusy] = useState(false);

  const customers = (customersQuery.data?.customers ?? []).filter((customer: any) =>
    !search.trim() ||
    [customer.name, customer.area, ...customer.jobs.map((job: any) => job.serviceName)]
      .some((value) => String(value ?? "").toLowerCase().includes(search.toLowerCase())),
  );
  const selected = (customersQuery.data?.customers ?? []).find((customer: any) => customer.customerId === selectedId) ?? null;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["business-customers"] });
  }

  async function addNote() {
    if (!selected || !note.trim()) return;
    setBusy(true);
    const result = await addBusinessCustomerNote({ data: { customerId: selected.customerId, note } });
    setBusy(false);
    if (!result.ok) { toast.error(result.error); return; }
    setNote("");
    toast.success("Customer note added");
    await refresh();
  }

  async function addFollowup() {
    if (!selected || !followup.title.trim() || !followup.dueAt) return;
    setBusy(true);
    const result = await scheduleBusinessCustomerFollowup({
      data: {
        customerId: selected.customerId,
        title: followup.title,
        dueAt: new Date(followup.dueAt).toISOString(),
        purpose: followup.purpose as "service" | "marketing",
        channel: followup.channel as "manual" | "email" | "sms" | "phone",
        ...(followup.note ? { note: followup.note } : {}),
      },
    });
    setBusy(false);
    if (!result.ok) { toast.error(result.error); return; }
    setFollowup({ title: "", dueAt: "", purpose: "service", channel: "manual", note: "" });
    toast.success("Follow-up scheduled");
    await refresh();
  }

  async function completeFollowup(followupId: string) {
    const result = await setBusinessCustomerFollowupStatus({ data: { followupId, status: "completed" } });
    if (!result.ok) { toast.error(result.error); return; }
    await refresh();
  }

  return <section className="partner-customers-ref">
    <header><h2>Customers</h2><span>{customersQuery.data?.customers.length ?? 0}</span></header>
    <label><Search/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search customers, areas or services…"/></label>
    <div>
      {customers.map((customer:any)=><button key={customer.customerId} type="button" onClick={()=>setSelectedId(customer.customerId)} className="partner-customer-row w-full text-left">
        <span className={"partner-customer-avatar "+(customer.preferred?"preferred":"")}><Users/></span>
        <span><strong>{customer.name}</strong><small>{customer.completedJobs} completed job{customer.completedJobs===1?"":"s"} · {customer.area} · {gbp(customer.revenuePence)}</small>{customer.preferred&&<em><Heart/> Explicitly wants to use you again</em>}</span><ArrowRight/>
      </button>)}
      {!customers.length&&<p className="business-empty">Completed-job customers will appear here automatically.</p>}
    </div>

    {selected && <section className="card-surface mt-5 p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold">{selected.name}</h3>
          <p className="text-sm text-muted-foreground">{selected.area} · {selected.completedJobs} completed job{selected.completedJobs===1?"":"s"}</p>
        </div>
        <button type="button" className="text-sm font-bold text-muted-foreground" onClick={()=>setSelectedId(null)}>Close</button>
      </header>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-border p-4">
          <strong>Relationship</strong>
          <p className="mt-1 text-sm text-muted-foreground">{selected.preferred ? "Customer explicitly said they would like to use this business again." : "No explicit repeat preference recorded."}</p>
        </div>
        <div className="rounded-xl border border-border p-4">
          <strong>Marketing consent</strong>
          <p className="mt-1 text-sm text-muted-foreground">Email: {selected.consent?.email_allowed && !selected.consent?.withdrawn_at ? "allowed" : "not allowed"} · SMS: {selected.consent?.sms_allowed && !selected.consent?.withdrawn_at ? "allowed" : "not allowed"}</p>
        </div>
      </div>

      <h4 className="mt-5 font-bold">Job history</h4>
      <div className="reference-rows mt-2">
        {selected.jobs.map((job:any)=><Link key={job.id} to="/partner/jobs/$bookingId" params={{bookingId:job.id}} className="reference-row"><span><strong>{job.serviceName}</strong><small>{job.completedAt ? ukDate(job.completedAt) : job.reference}{job.agreedPricePence ? " · "+gbp(job.agreedPricePence) : ""}</small></span><ArrowRight/></Link>)}
      </div>

      {customersQuery.data?.canEdit && <>
        <h4 className="mt-5 font-bold">Private business notes</h4>
        <div className="mt-2 flex gap-2"><input className="min-h-11 flex-1 rounded-lg border border-input px-3" value={note} onChange={e=>setNote(e.target.value)} placeholder="Add a note about this customer…" maxLength={2000}/><Button type="button" disabled={busy||!note.trim()} onClick={addNote}>Add</Button></div>
        <div className="mt-2 space-y-2">{selected.notes.slice(0,10).map((item:any)=><div key={item.id} className="rounded-lg bg-muted px-3 py-2 text-sm">{item.note}<small className="mt-1 block text-muted-foreground">{ukDate(item.created_at)}</small></div>)}</div>

        <h4 className="mt-5 font-bold">Follow-up</h4>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <input className="min-h-11 rounded-lg border border-input px-3" value={followup.title} onChange={e=>setFollowup({...followup,title:e.target.value})} placeholder="Follow-up title"/>
          <input className="min-h-11 rounded-lg border border-input px-3" type="datetime-local" value={followup.dueAt} onChange={e=>setFollowup({...followup,dueAt:e.target.value})}/>
          <select className="min-h-11 rounded-lg border border-input px-3" value={followup.purpose} onChange={e=>setFollowup({...followup,purpose:e.target.value})}><option value="service">Service follow-up</option><option value="marketing">Marketing</option></select>
          <select className="min-h-11 rounded-lg border border-input px-3" value={followup.channel} onChange={e=>setFollowup({...followup,channel:e.target.value})}><option value="manual">Manual reminder</option><option value="phone">Phone</option><option value="email">Email</option><option value="sms">SMS</option></select>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Marketing email/SMS follow-ups are blocked by the server unless the customer has explicitly consented to that channel.</p>
        <Button type="button" className="mt-3" disabled={busy||!followup.title||!followup.dueAt} onClick={addFollowup}>Schedule follow-up</Button>
      </>}

      {selected.followups.length>0&&<div className="mt-5"><h4 className="font-bold">Scheduled follow-ups</h4><div className="mt-2 space-y-2">{selected.followups.map((item:any)=><div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"><span><strong>{item.title}</strong><small className="block text-muted-foreground">{new Date(item.due_at).toLocaleString("en-GB")} · {item.purpose} · {item.channel} · {item.status}</small></span>{item.status==="open"&&customersQuery.data?.canEdit&&<button type="button" className="text-sm font-bold text-primary" onClick={()=>completeFollowup(item.id)}>Mark done</button>}</div>)}</div></div>}
    </section>}
  </section>;
}

function Metric({ label, value }: { label: string; value: string }) { return <article className="business-workspace-metric"><span>{label}</span><strong>{value}</strong></article>; }
function NotFound() { return <div className="flex min-h-screen items-center justify-center bg-background p-6"><div className="text-center"><strong>Workspace not found</strong><Link to="/partner/business" className="mt-4 block text-primary">Return to Business OS</Link></div></div>; }

const EXPENSE_CATEGORIES = ["materials", "fuel", "tools", "travel", "subcontractor", "insurance", "other"] as const;
const today = () => new Date().toISOString().slice(0, 10);
const toPence = (value: string) => Math.round(Number(value || 0) * 100);

function useFinance() {
  const queryClient = useQueryClient();
  const finance = useQuery({ queryKey: ["partner-finance"], queryFn: () => getPartnerFinance() });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["partner-finance"] });
  return { finance, refresh };
}

function JobSelect({ jobs, value, onChange }: { jobs: any[]; value: string; onChange: (v: string) => void }) {
  return (
    <label className="business-field">
      <span>Linked Shields job (optional)</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Not linked to a job</option>
        {jobs.map((job) => (
          <option key={job.id} value={job.id}>
            {job.reference} · {(job.services as { name?: string } | null)?.name ?? "Shields job"}
          </option>
        ))}
      </select>
    </label>
  );
}

function InvoicesPanel({ initialBookingId, initialCustomerName, autoOpen = false }: { initialBookingId?: string | undefined; initialCustomerName?: string | undefined; autoOpen?: boolean }) {
  const { finance, refresh } = useFinance();
  const data = finance.data;
  const [open, setOpen] = useState(autoOpen);
  const [filter, setFilter] = useState<"all"|"unpaid"|"paid"|"overdue">("all");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ bookingId: "", customerName: "", customerEmail: "", description: "", amount: "", vat: "", dueDate: "" });
  const [prefilledBookingId, setPrefilledBookingId] = useState<string | null>(null);

  useEffect(() => {
    if (!initialBookingId || prefilledBookingId === initialBookingId || !data?.jobs?.length) return;
    const job = data.jobs.find((item: any) => item.id === initialBookingId);
    if (!job) return;
    const serviceName = (job.services as { name?: string } | null)?.name ?? "Shields job";
    setForm((current) => ({
      ...current,
      bookingId: job.id,
      customerName: current.customerName || initialCustomerName || "",
      description: current.description || `${serviceName} · ${job.reference}`,
      amount: current.amount || (job.agreed_price_pence != null ? (Number(job.agreed_price_pence) / 100).toFixed(2) : ""),
    }));
    setOpen(true);
    setPrefilledBookingId(initialBookingId);
  }, [data?.jobs, initialBookingId, initialCustomerName, prefilledBookingId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await createPartnerInvoice({
        data: {
          bookingId: form.bookingId || null,
          customerName: form.customerName,
          ...(form.customerEmail ? { customerEmail: form.customerEmail } : {}),
          description: form.description,
          subtotalPence: toPence(form.amount),
          vatPence: toPence(form.vat),
          ...(form.dueDate ? { dueDate: form.dueDate } : {}),
        },
      });
      if (!result.ok) { toast.error(result.error); return; }
      toast.success("Invoice " + result.invoiceNumber + " created");
      setForm({ bookingId: "", customerName: "", customerEmail: "", description: "", amount: "", vat: "", dueDate: "" });
      setOpen(false);
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create the invoice");
    } finally { setBusy(false); }
  }

  async function mark(invoiceId: string, status: "sent" | "paid" | "void") {
    if (busy) return;
    setBusy(true);
    try {
      const result = await setPartnerInvoiceStatus({ data: { invoiceId, status } });
      if (!result.ok) { toast.error(result.error); return; }
      toast.success(status === "paid" ? "Invoice marked paid" : status === "void" ? "Invoice voided" : "Invoice marked sent");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update this invoice");
    } finally {
      setBusy(false);
    }
  }
  async function send(invoiceId: string) {
    if (busy) return;
    setBusy(true);
    try {
      const result = await sendPartnerInvoice({ data: { invoiceId } });
      if (!result.ok) { toast.error(result.error); return; }
      toast.success("Invoice sent");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not send this invoice");
    } finally {
      setBusy(false);
    }
  }

  const invoices = data?.invoices ?? [];
  const outstanding = invoices.filter((row: any) => row.status !== "paid" && row.status !== "void").reduce((sum: number, row: any) => sum + row.total_pence, 0);
  const paid = invoices.filter((row: any) => row.status === "paid").reduce((sum: number, row: any) => sum + row.total_pence, 0);

  const todayDate = new Date().toISOString().slice(0,10);
  const isOverdue = (row:any) => row.status !== "paid" && row.status !== "void" && Boolean(row.due_date && row.due_date < todayDate);
  const unpaidCount = invoices.filter((row:any)=>row.status!=="paid"&&row.status!=="void").length;
  const filteredInvoices = invoices.filter((row:any)=>filter==="all" ? true : filter==="paid" ? row.status==="paid" : filter==="overdue" ? isOverdue(row) : row.status!=="paid"&&row.status!=="void");

  return <section className="partner-invoices-ref">
    <header className="partner-invoices-title">
      <h2>Invoices</h2>
      <button type="button" aria-label={open?"Close new invoice form":"Create invoice"} onClick={()=>setOpen(v=>!v)}>{open?"×":"+"}</button>
    </header>
    <nav className="partner-invoice-tabs" aria-label="Invoice filters">
      <button className={filter==="all"?"active":""} onClick={()=>setFilter("all")}>All</button>
      <button className={filter==="unpaid"?"active":""} onClick={()=>setFilter("unpaid")}>Unpaid ({unpaidCount})</button>
      <button className={filter==="paid"?"active":""} onClick={()=>setFilter("paid")}>Paid</button>
      <button className={filter==="overdue"?"active":""} onClick={()=>setFilter("overdue")}>Overdue</button>
    </nav>
    {open && <form className="business-form partner-invoice-form" onSubmit={submit}>
      <JobSelect jobs={data?.jobs ?? []} value={form.bookingId} onChange={(bookingId) => setForm({ ...form, bookingId })} />
      <label className="business-field"><span>Customer name</span><input required maxLength={160} value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} /></label>
      <label className="business-field"><span>Customer email (optional)</span><input type="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} /></label>
      <label className="business-field"><span>What the invoice covers</span><input required maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
      <label className="business-field"><span>Amount before VAT (£)</span><input required inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></label>
      <label className="business-field"><span>VAT (£)</span><input inputMode="decimal" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></label>
      <label className="business-field"><span>Due date (optional)</span><input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} /></label>
      <button type="submit" className="business-action" disabled={busy}>{busy ? "Saving…" : "Create invoice"}</button>
    </form>}
    <div className="partner-invoice-list">
      {filteredInvoices.map((row:any)=><article key={row.id} className="partner-invoice-row">
        <span className="partner-invoice-icon"><Receipt/></span>
        <span className="partner-invoice-copy"><strong>{row.invoice_number}</strong><small>{row.customer_name}</small></span>
        <span className="partner-invoice-money"><strong>{gbp(row.total_pence)}</strong><em className={row.status==="paid"?"paid":isOverdue(row)?"overdue":"unpaid"}>{row.status==="paid" ? (row.paid_at ? "Paid "+ukDate(row.paid_at) : "Paid") : isOverdue(row) ? "Overdue" : "Unpaid"}</em></span>
        <span className="partner-invoice-chevron"><ArrowRight/></span>
        <span className="partner-invoice-actions">
          {row.status==="draft"&&row.customer_email&&<button type="button" disabled={busy} onClick={()=>send(row.id)}>Send invoice</button>}{row.status==="draft"&&!row.customer_email&&<button type="button" disabled={busy} onClick={()=>mark(row.id,"sent")}>Mark sent</button>}
          {row.status!=="paid"&&row.status!=="void"&&<button type="button" disabled={busy} onClick={()=>mark(row.id,"paid")}>Mark paid</button>}
        </span>
      </article>)}
      {!filteredInvoices.length&&<div className="business-empty"><strong>No {filter==="all"?"":filter+" "}invoices</strong><span>{filter==="all"?"Tap + to create your first invoice.":"Invoices matching this filter will appear here."}</span></div>}
    </div>
  </section>;
}

function ExpensesPanel() {
  const { finance, refresh } = useFinance();
  const data = finance.data;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const emptyForm = { bookingId: "", category: "materials", supplier: "", description: "", amount: "", vat: "", incurredOn: today(), receiptFile: null as File | null, existingReceiptPath: null as string | null };
  const [form, setForm] = useState(emptyForm);

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    let uploadedPath: string | undefined;
    try {
      if (form.receiptFile) uploadedPath = await uploadBusinessReceipt(form.receiptFile);
      const payload = {
        bookingId: form.bookingId || null,
        category: form.category as (typeof EXPENSE_CATEGORIES)[number],
        ...(form.supplier ? { supplier: form.supplier } : {}),
        description: form.description,
        amountPence: toPence(form.amount),
        vatPence: toPence(form.vat),
        incurredOn: form.incurredOn,
        ...(uploadedPath ? { receiptPath: uploadedPath } : {}),
      };
      const result = editingId
        ? await updatePartnerExpense({
            data: {
              expenseId: editingId,
              ...payload,
              ...(form.receiptFile ? { receiptPath: uploadedPath! } : {}),
            },
          })
        : await createPartnerExpense({ data: payload });

      if (!result.ok) {
        if (uploadedPath) await removeUploadedFile("business-receipts", uploadedPath);
        toast.error(result.error);
        return;
      }
      toast.success(editingId ? "Expense updated" : "Expense recorded");
      resetForm();
      await refresh();
    } catch (error) {
      if (uploadedPath) await removeUploadedFile("business-receipts", uploadedPath);
      toast.error(error instanceof Error ? error.message : "Could not save the expense");
    } finally {
      setBusy(false);
    }
  }

  function edit(row: any) {
    setEditingId(row.id);
    setForm({
      bookingId: row.booking_id ?? "",
      category: row.category,
      supplier: row.supplier ?? "",
      description: row.description,
      amount: (Number(row.amount_pence ?? 0) / 100).toFixed(2),
      vat: (Number(row.vat_pence ?? 0) / 100).toFixed(2),
      incurredOn: row.incurred_on,
      receiptFile: null,
      existingReceiptPath: row.receipt_path ?? null,
    });
    setOpen(true);
  }

  async function remove(expenseId: string) {
    if (busy) return;
    setBusy(true);
    try {
      const result = await deletePartnerExpense({ data: { expenseId } });
      if (!result.ok) { toast.error(result.error); return; }
      toast.success(result.cleanupPending
        ? "Expense removed. Shields will finish receipt cleanup automatically."
        : "Expense removed");
      if (editingId === expenseId) resetForm();
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove this expense");
    } finally {
      setBusy(false);
    }
  }

  async function openReceipt(expenseId: string) {
    try {
      const result = await getPartnerExpenseReceiptUrl({ data: { expenseId } });
      if (!result.ok) { toast.error(result.error); return; }
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not open this receipt");
    }
  }

  const expenses = data?.expenses ?? [];
  const total = expenses.reduce((sum: number, row: any) => sum + row.amount_pence, 0);
  const vat = expenses.reduce((sum: number, row: any) => sum + row.vat_pence, 0);
  const thisMonth = expenses.filter((row: any) => row.incurred_on >= new Date().toISOString().slice(0, 8) + "01").reduce((sum: number, row: any) => sum + row.amount_pence, 0);

  return <section className="business-data-panel">
    <header>
      <div><p className="technical-label">DIRECT COSTS</p><h2>Expenses</h2></div>
      <button type="button" className="business-action" onClick={() => { if (open) resetForm(); else setOpen(true); }}>{open ? "Close" : "Add expense"}</button>
    </header>
    <div className="business-finance-grid">
      <Metric label="TOTAL RECORDED" value={gbp(total)} />
      <Metric label="THIS MONTH" value={gbp(thisMonth)} />
      <Metric label="VAT RECORDED" value={gbp(vat)} />
    </div>
    {open && <form className="business-form" onSubmit={submit}>
      <h3 className="font-bold">{editingId ? "Edit expense" : "Add expense"}</h3>
      <JobSelect jobs={data?.jobs ?? []} value={form.bookingId} onChange={(bookingId) => setForm({ ...form, bookingId })} />
      <label className="business-field"><span>Category</span><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{EXPENSE_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}</select></label>
      <label className="business-field"><span>Supplier (optional)</span><input maxLength={160} value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></label>
      <label className="business-field"><span>Description</span><input required maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
      <label className="business-field"><span>Amount including VAT (£)</span><input required inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></label>
      <label className="business-field"><span>VAT included in that amount (£)</span><input inputMode="decimal" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></label>
      <label className="business-field"><span>Date</span><input type="date" required value={form.incurredOn} onChange={(e) => setForm({ ...form, incurredOn: e.target.value })} /></label>
      <label className="business-field"><span>Receipt (optional)</span><input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => setForm({ ...form, receiptFile: e.target.files?.[0] ?? null })} />{form.existingReceiptPath && !form.receiptFile && <small>A receipt is already attached. Choose another file to replace it.</small>}</label>
      <button type="submit" className="business-action" disabled={busy}>{busy ? "Saving…" : editingId ? "Save changes" : "Record expense"}</button>
    </form>}
    <div className="business-record-list">
      {expenses.map((row: any) => <article key={row.id} className="business-live-row">
        <span className="business-record-icon"><Coins /></span>
        <span><strong>{row.description}</strong><small>{row.category}{row.supplier ? " · " + row.supplier : ""} · {ukDate(row.incurred_on)}{row.vat_pence ? " · VAT " + gbp(row.vat_pence) : ""}</small></span>
        <span className="business-row-value">{gbp(row.amount_pence)}</span>
        <span className="business-row-actions">
          {row.receipt_path && <button type="button" onClick={() => openReceipt(row.id)}><Paperclip className="h-3.5 w-3.5" /> Receipt</button>}
          <button type="button" onClick={() => edit(row)}><Pencil className="h-3.5 w-3.5" /> Edit</button>
          <button type="button" disabled={busy} onClick={() => remove(row.id)}>Remove</button>
        </span>
      </article>)}
      {!expenses.length && <div className="business-empty"><strong>No expenses yet</strong><span>Record materials, fuel and subcontractor costs to see true job profit.</span></div>}
    </div>
  </section>;
}

function CostingPanel() {
  const { finance } = useFinance();
  const data = finance.data;
  const rows = data?.profitability ?? [];
  const revenue = rows.reduce((sum: number, row: any) => sum + Number(row.revenuePence ?? 0), 0);
  const cost = rows.reduce((sum: number, row: any) => sum + Number(row.recordedCostPence ?? 0), 0);
  const expenseVat = rows.reduce((sum: number, row: any) => sum + Number(row.expenseVatPence ?? 0), 0);
  const profit = revenue - cost;
  const margin = revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : null;

  return <section className="business-data-panel">
    <header><div><p className="technical-label">JOB ECONOMICS</p><h2>Job costing and profit</h2></div></header>
    <div className="business-finance-grid">
      <Metric label="AGREED REVENUE" value={gbp(revenue)} />
      <Metric label="RECORDED COSTS" value={cost > 0 ? gbp(cost) : "Add costs"} />
      <Metric label="PROFIT" value={cost > 0 ? gbp(profit) : "Pending costs"} />
      <Metric label="MARGIN" value={margin == null ? "—" : margin.toFixed(1) + "%"} />
    </div>
    {expenseVat > 0 && <p className="business-footnote">Recorded expense VAT: {gbp(expenseVat)}. Shields does not assume VAT is reclaimable; operational profit uses the full recorded expense amount.</p>}
    <div className="business-record-list">
      {rows.slice(0, 20).map((row: any) => <Link key={row.bookingId} to="/partner/jobs/$bookingId" params={{ bookingId: row.bookingId }} className="business-live-row">
        <span className="business-record-icon"><TrendingUp /></span>
        <span><strong>{row.serviceName}</strong><small>{row.reference} · revenue {row.revenuePence ? gbp(row.revenuePence) : "price pending"}{row.completeCostData ? " · costs " + gbp(row.recordedCostPence) : " · no costs recorded"}{row.labourPence ? " · labour " + gbp(row.labourPence) : ""}</small></span>
        <span className="business-row-value">{row.completeCostData && row.revenuePence ? gbp(row.profitPence) + (row.marginPercent != null ? " · " + Number(row.marginPercent).toFixed(1) + "%" : "") : "—"}</span>
        <ArrowRight />
      </Link>)}
      {!rows.length && <div className="business-empty"><strong>No costing records yet</strong><span>Costing appears when you win Shields work and record expenses or labour against it.</span></div>}
    </div>
    <p className="business-footnote">These figures are calculated on the server from genuine agreed GBP prices, recorded gross expenses and logged labour cost. They are operational estimates, not tax advice.</p>
  </section>;
}
